import { z } from 'zod';
import { uuidSchema } from './comum.ts';
import { cotaSchema } from './menu-favoritos.ts';
import { agrupamentoConsumoObraSchema, ORDENS_CONSUMO_OBRA } from './relatorio-consumo-obra.ts';
import { situacaoTituloSchema } from './titulos.ts';

/**
 * Os filtros que cada usuário salva como **padrão** de um relatório
 * (`.spec/015`, decisão 78).
 *
 * Quem abre o relatório toda segunda-feira com "Construtora Norte, em aberto,
 * vencendo nos próximos sete dias" não deveria refazer os três cliques a cada
 * vez. O padrão é da **pessoa** e do **relatório** — uma linha por par —, e fica
 * no servidor pelo motivo dos favoritos do menu (decisão 75): trocar de
 * computador ou limpar o navegador não pode apagar o que ela montou.
 *
 * ## Datas: só período relativo
 *
 * O que se salva de um período é o **nome** dele — "este mês", "próximos 30
 * dias" —, e não as datas. Salvar "01/10 a 31/10" produziria em novembro um
 * relatório de outubro que ninguém pediu. As datas digitadas à mão continuam
 * valendo na tela e **não** entram no padrão: é o preço de o padrão nunca
 * envelhecer. Quem resolve o período em datas é o **cliente**, com a data do
 * navegador, e a rota do relatório continua recebendo `de`/`ate` como antes — o
 * relatório não sabe que existe padrão.
 *
 * ## O servidor valida por relatório
 *
 * Cada relatório declara aqui o formato do seu padrão, e a rota recusa o que
 * não cabe nele. Na leitura, o padrão gravado passa de novo pelo formato
 * **atual**: se o relatório mudou e o padrão antigo deixou de caber, ele volta
 * como `null` — o usuário vê o relatório sem padrão, e não um erro.
 */

/* -------------------------------------------------------------------------- */
/* Períodos relativos                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Os períodos relativos, na ordem em que aparecem no seletor.
 *
 * A semana é de **segunda a domingo**, a do calendário comercial. "Até ontem" é
 * o vencido; "a partir de hoje", o que ainda vai vencer.
 */
export const PERIODOS_RELATIVOS = [
  'HOJE',
  'ESTA_SEMANA',
  'PROXIMA_SEMANA',
  'SEMANA_PASSADA',
  'PROXIMOS_7_DIAS',
  'PROXIMOS_30_DIAS',
  'ULTIMOS_7_DIAS',
  'ULTIMOS_30_DIAS',
  'ESTE_MES',
  'PROXIMO_MES',
  'MES_PASSADO',
  'ESTE_ANO',
  'ATE_ONTEM',
  'A_PARTIR_DE_HOJE',
] as const;

export const periodoRelativoSchema = z.enum(PERIODOS_RELATIVOS);
export type PeriodoRelativo = z.infer<typeof periodoRelativoSchema>;

export const ROTULO_PERIODO_RELATIVO: Readonly<Record<PeriodoRelativo, string>> = {
  HOJE: 'Hoje',
  ESTA_SEMANA: 'Esta semana',
  PROXIMA_SEMANA: 'Próxima semana',
  SEMANA_PASSADA: 'Semana passada',
  PROXIMOS_7_DIAS: 'Próximos 7 dias',
  PROXIMOS_30_DIAS: 'Próximos 30 dias',
  ULTIMOS_7_DIAS: 'Últimos 7 dias',
  ULTIMOS_30_DIAS: 'Últimos 30 dias',
  ESTE_MES: 'Este mês',
  PROXIMO_MES: 'Próximo mês',
  MES_PASSADO: 'Mês passado',
  ESTE_ANO: 'Este ano',
  ATE_ONTEM: 'Até ontem',
  A_PARTIR_DE_HOJE: 'De hoje em diante',
};

/** Um período em datas. `null` numa ponta é "sem limite" daquele lado. */
export interface PeriodoEmDatas {
  de: string | null;
  ate: string | null;
}

/*
  Aritmética de data sobre `Date` em **UTC**, e só para somar dias: a data de
  negócio não tem hora nem fuso, e fazer a conta no fuso local faria o horário
  de verão de algum país mover um dia. O "hoje" chega de fora, já como texto.
*/
function paraData(iso: string): Date {
  const [ano = 0, mes = 1, dia = 1] = iso.split('-').map(Number);
  return new Date(Date.UTC(ano, mes - 1, dia));
}

function paraIso(data: Date): string {
  return data.toISOString().slice(0, 10);
}

function somarDias(iso: string, dias: number): string {
  const data = paraData(iso);
  data.setUTCDate(data.getUTCDate() + dias);
  return paraIso(data);
}

/** O mês de `iso` deslocado de `deslocamento` meses: primeiro e último dia. */
function mes(iso: string, deslocamento: number): PeriodoEmDatas {
  const data = paraData(iso);
  const primeiro = new Date(Date.UTC(data.getUTCFullYear(), data.getUTCMonth() + deslocamento, 1));
  const ultimo = new Date(
    Date.UTC(data.getUTCFullYear(), data.getUTCMonth() + deslocamento + 1, 0),
  );
  return { de: paraIso(primeiro), ate: paraIso(ultimo) };
}

/** A semana de segunda a domingo que contém `iso`, deslocada de `semanas`. */
function semana(iso: string, semanas: number): PeriodoEmDatas {
  // `getUTCDay`: domingo é 0. Segunda vira 0 dias para trás, domingo vira 6.
  const recuo = (paraData(iso).getUTCDay() + 6) % 7;
  const segunda = somarDias(iso, -recuo + semanas * 7);
  return { de: segunda, ate: somarDias(segunda, 6) };
}

/**
 * O período relativo em datas, a partir de `hoje` (`AAAA-MM-DD`).
 *
 * Função pura, com `hoje` recebido: o teste fixa o dia, e quem chama decide de
 * que relógio ele vem — na tela, o do navegador, que é o dia de quem está
 * olhando.
 */
export function resolverPeriodoRelativo(periodo: PeriodoRelativo, hoje: string): PeriodoEmDatas {
  const ano = hoje.slice(0, 4);
  const tabela: Record<PeriodoRelativo, () => PeriodoEmDatas> = {
    HOJE: () => ({ de: hoje, ate: hoje }),
    ESTA_SEMANA: () => semana(hoje, 0),
    PROXIMA_SEMANA: () => semana(hoje, 1),
    SEMANA_PASSADA: () => semana(hoje, -1),
    PROXIMOS_7_DIAS: () => ({ de: hoje, ate: somarDias(hoje, 6) }),
    PROXIMOS_30_DIAS: () => ({ de: hoje, ate: somarDias(hoje, 29) }),
    ULTIMOS_7_DIAS: () => ({ de: somarDias(hoje, -6), ate: hoje }),
    ULTIMOS_30_DIAS: () => ({ de: somarDias(hoje, -29), ate: hoje }),
    ESTE_MES: () => mes(hoje, 0),
    PROXIMO_MES: () => mes(hoje, 1),
    MES_PASSADO: () => mes(hoje, -1),
    ESTE_ANO: () => ({ de: `${ano}-01-01`, ate: `${ano}-12-31` }),
    ATE_ONTEM: () => ({ de: null, ate: somarDias(hoje, -1) }),
    A_PARTIR_DE_HOJE: () => ({ de: hoje, ate: null }),
  };
  return tabela[periodo]();
}

/* -------------------------------------------------------------------------- */
/* O padrão de cada relatório                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Relatório de contas a pagar (`PAG·03`).
 *
 * Empresa, fornecedor e situação como estão; os dois períodos só como relativo;
 * e as opções de exibir o subtotal por vencimento e o resumo. Tudo opcional — o padrão salvo com a tela vazia
 * é "abrir vazio", e é um padrão legítimo.
 */
export const filtroSalvoContasPagarSchema = z
  .object({
    empresaId: uuidSchema.optional(),
    fornecedorId: uuidSchema.optional(),
    situacao: situacaoTituloSchema.optional(),
    emissao: periodoRelativoSchema.optional(),
    vencimento: periodoRelativoSchema.optional(),
    exibirResumo: z.boolean().prefault(false),
    // Ligado por padrão: o padrão gravado antes da opção existir abre como abria.
    exibirSubtotal: z.boolean().prefault(true),
  })
  .strict();

export type FiltroSalvoContasPagar = z.infer<typeof filtroSalvoContasPagarSchema>;

/**
 * Relatório de contas a receber (`REC·03`).
 *
 * O formato do `PAG·03` com o cliente no lugar do fornecedor. Separado, e não o
 * mesmo schema com o campo renomeado: os dois relatórios evoluem cada um por si,
 * e o padrão de um nunca deve caber no outro.
 */
export const filtroSalvoContasReceberSchema = z
  .object({
    empresaId: uuidSchema.optional(),
    clienteId: uuidSchema.optional(),
    situacao: situacaoTituloSchema.optional(),
    emissao: periodoRelativoSchema.optional(),
    vencimento: periodoRelativoSchema.optional(),
    exibirResumo: z.boolean().prefault(false),
    // Ligado por padrão: o padrão gravado antes da opção existir abre como abria.
    exibirSubtotal: z.boolean().prefault(true),
  })
  .strict();

export type FiltroSalvoContasReceber = z.infer<typeof filtroSalvoContasReceberSchema>;

/**
 * Relatório de pagamentos (`PAG·04`).
 *
 * Empresa, fornecedor e conta corrente como estão; o período de pagamento só
 * como relativo; e as duas opções de exibição. "Quebrar por data" nasce
 * **ligado** — é o desenho do pedido —, e o resumo desligado, como nos outros.
 */
export const filtroSalvoPagamentosSchema = z
  .object({
    empresaId: uuidSchema.optional(),
    fornecedorId: uuidSchema.optional(),
    contaCorrenteId: uuidSchema.optional(),
    pagamento: periodoRelativoSchema.optional(),
    quebrarPorData: z.boolean().prefault(true),
    exibirResumo: z.boolean().prefault(false),
  })
  .strict();

export type FiltroSalvoPagamentos = z.infer<typeof filtroSalvoPagamentosSchema>;

/**
 * Relatório de recebimentos (`REC·04`) — o formato do `PAG·04` com o cliente no
 * lugar do fornecedor. Separado pela razão do `REC·03`: o padrão de um nunca
 * deve caber no outro.
 */
export const filtroSalvoRecebimentosSchema = z
  .object({
    empresaId: uuidSchema.optional(),
    clienteId: uuidSchema.optional(),
    contaCorrenteId: uuidSchema.optional(),
    recebimento: periodoRelativoSchema.optional(),
    quebrarPorData: z.boolean().prefault(true),
    exibirResumo: z.boolean().prefault(false),
  })
  .strict();

export type FiltroSalvoRecebimentos = z.infer<typeof filtroSalvoRecebimentosSchema>;

/** Relatório de consumo de insumos na obra (`CTR·05`). */
export const filtroSalvoConsumoObraSchema = z
  .object({
    empresaId: uuidSchema.optional(),
    obraId: uuidSchema.optional(),
    periodo: periodoRelativoSchema.optional(),
    agrupamento: agrupamentoConsumoObraSchema.prefault('INSUMO'),
    exibirValor: z.boolean().prefault(true),
    ordem: z.enum(ORDENS_CONSUMO_OBRA).prefault('NOME'),
  })
  .strict();

export type FiltroSalvoConsumoObra = z.infer<typeof filtroSalvoConsumoObraSchema>;

/**
 * Os relatórios que aceitam padrão salvo, pela cota, e o formato de cada um.
 *
 * A cota é a chave porque é o endereço estável da tela (ver `menu-favoritos.ts`).
 * Relatório novo que queira padrão entra aqui — e só aqui: a tabela, a rota e o
 * hook da tela são genéricos.
 */
export const SCHEMAS_DE_FILTRO_SALVO = {
  'PAG·03': filtroSalvoContasPagarSchema,
  'PAG·04': filtroSalvoPagamentosSchema,
  'REC·03': filtroSalvoContasReceberSchema,
  'REC·04': filtroSalvoRecebimentosSchema,
  'CTR·05': filtroSalvoConsumoObraSchema,
} as const;

export type CotaComFiltroSalvo = keyof typeof SCHEMAS_DE_FILTRO_SALVO;

export const COTAS_COM_FILTRO_SALVO = Object.keys(SCHEMAS_DE_FILTRO_SALVO) as CotaComFiltroSalvo[];

/**
 * A cota no caminho da rota. Estrita ao catálogo acima: cota de relatório sem
 * padrão é recusada (422), e não vira um padrão gravado que tela nenhuma lê.
 */
export const paramsFiltroSalvoSchema = z
  .object({
    cota: cotaSchema.refine(
      (cota): cota is CotaComFiltroSalvo => cota in SCHEMAS_DE_FILTRO_SALVO,
      'Este relatório não tem filtro salvo.',
    ),
  })
  .strict();

/** O corpo do `PUT`: o filtro, que a rota confere contra o formato do relatório. */
export const gravarFiltroSalvoSchema = z
  .object({ filtro: z.record(z.string(), z.unknown()) })
  .strict();

export type GravarFiltroSalvo = z.infer<typeof gravarFiltroSalvoSchema>;

/**
 * A resposta. `filtro` é `null` quando não há padrão — ou quando o gravado não
 * cabe mais no formato atual do relatório.
 */
export const filtroSalvoSchema = z.object({
  cota: cotaSchema,
  filtro: z.record(z.string(), z.unknown()).nullable(),
  atualizadoEm: z.string().nullable(),
});

export type FiltroSalvo = z.infer<typeof filtroSalvoSchema>;

/**
 * Confere um filtro contra o formato do relatório. Devolve o filtro já
 * normalizado (com os padrões preenchidos), ou `null` quando não cabe.
 *
 * Usada nos dois sentidos: na gravação a rota recusa o `null`; na leitura ele
 * vira "sem padrão".
 */
export function filtroSalvoValido(
  cota: CotaComFiltroSalvo,
  filtro: unknown,
): Record<string, unknown> | null {
  const resultado = SCHEMAS_DE_FILTRO_SALVO[cota].safeParse(filtro);
  return resultado.success ? resultado.data : null;
}
